import {
  type Column,
  DownloadButton,
  ExportApiReportFormatters,
} from '@ovComponents/3-pattern/downloadButton/downloadButton';
import { UserContext } from '@providers/userContextProvider';
import dayjs from 'dayjs';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExposureLevel } from './productAumExposureTable';
import { FETCH_PRODUCT_AUM_EXPOSURE_SOURCE } from './queries';

const sanitizeFileNamePart = (value: string): string =>
  value.replace(/[^a-zA-Z0-9_-]/g, '_');

const getNameColumnsByLevel = (
  exposureLevel: ExposureLevel,
  t: (key: string) => string,
): Column[] => {
  const rankColumn: Column = {
    gqlAlias: 'rank',
    header: t('homePageConfiguration:productAumExposure.columns.rank'),
  };
  const marketValueColumn: Column = {
    gqlAlias: 'marketValueCents',
    header: t('homePageConfiguration:productAumExposure.columns.marketValue'),
    formatter: ExportApiReportFormatters.DIVIDE_BY_100,
  };

  switch (exposureLevel) {
    case 'HOUSEHOLD':
      return [
        rankColumn,
        {
          gqlAlias: 'household.name',
          header: t(
            'homePageConfiguration:productAumExposure.columns.householdName',
          ),
        },
        marketValueColumn,
      ];
    case 'ENTITY':
      return [
        rankColumn,
        {
          gqlAlias: 'user.entityName',
          header: t(
            'homePageConfiguration:productAumExposure.columns.entityName',
          ),
        },
        {
          gqlAlias: 'user.firstName',
          header: t(
            'homePageConfiguration:productAumExposure.columns.firstName',
          ),
        },
        {
          gqlAlias: 'user.lastName',
          header: t(
            'homePageConfiguration:productAumExposure.columns.lastName',
          ),
        },
        marketValueColumn,
      ];
    case 'ACCOUNT':
      return [
        rankColumn,
        {
          gqlAlias: 'account.user.entityName',
          header: t(
            'homePageConfiguration:productAumExposure.columns.entityName',
          ),
        },
        {
          gqlAlias: 'account.user.firstName',
          header: t(
            'homePageConfiguration:productAumExposure.columns.firstName',
          ),
        },
        {
          gqlAlias: 'account.user.lastName',
          header: t(
            'homePageConfiguration:productAumExposure.columns.lastName',
          ),
        },
        {
          gqlAlias: 'account.type',
          header: t(
            'homePageConfiguration:productAumExposure.columns.accountType',
          ),
        },
        {
          gqlAlias: 'account.custodianAccountNumber',
          header: t(
            'homePageConfiguration:productAumExposure.columns.accountNumber',
          ),
        },
        marketValueColumn,
      ];
  }
};

const DownloadProductAumExposure = ({
  filter,
  exposureLevel,
  ticker,
}: {
  filter: Record<string, unknown>;
  exposureLevel: ExposureLevel;
  ticker?: string;
}) => {
  const { t } = useTranslation(['homePageConfiguration']);
  const { activeOrganization, baseCurrency } = useContext(UserContext);
  const columns = useMemo(
    () => getNameColumnsByLevel(exposureLevel, t),
    [exposureLevel, t],
  );
  const sanitizedTicker = ticker ? sanitizeFileNamePart(ticker) : '';
  const fileName = [
    t('homePageConfiguration:productAumExposure.exportFileName'),
    sanitizedTicker,
    dayjs().format('YYYY-MM-DD'),
  ]
    .filter(Boolean)
    .join('_');

  return (
    <DownloadButton
      key={exposureLevel}
      configurable
      gql={FETCH_PRODUCT_AUM_EXPOSURE_SOURCE}
      fileName={fileName}
      filter={{
        ...filter,
        organizationId: activeOrganization.id,
        currency: baseCurrency,
        objectType: exposureLevel,
      }}
      queryPath='fetchProductAumExposure'
      datasetPath='exposures'
      columns={columns}
      sortField='marketValueCents'
      sortDesc
      progressTitle={t(
        'homePageConfiguration:productAumExposure.exportProgressTitle',
      )}
    />
  );
};

export default DownloadProductAumExposure;
